import { Search, List, Calendar, Layout, BarChart2, FileText, ClipboardList, Table, Activity, Monitor, Map, Clock, Users, Globe, FileSpreadsheet } from 'lucide-react'
import { Input } from './ui/Input'
import { useState, useRef, useLayoutEffect } from 'react'
import { ViewItem, ViewItemSimple } from './ViewSelectorItems'

interface ViewSelectorProps {
    isOpen: boolean
    onClose: () => void
}

export function ViewSelector({ isOpen }: ViewSelectorProps) {
    const [isAnimating, setIsAnimating] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({ left: 0 })

    // Derived state pattern for animation
    if (isOpen && !isAnimating) {
        setIsAnimating(true)
    }

    useLayoutEffect(() => {
        if (isOpen && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth

            // Check if menu overflows right edge
            if (rect.right > viewportWidth) {
                // If overflows, align to right edge of parent or shift left
                // Simple strategy: switch to right alignment if space permits, or calculate offset
                const overflowAmount = rect.right - viewportWidth + 20 // 20px padding
                setPositionStyle({ left: `-${overflowAmount}px` })
            } else {
                setPositionStyle({ left: 0 })
            }
        }
    }, [isOpen])

    const handleAnimationEnd = () => {
        if (!isOpen) {
            setIsAnimating(false)
        }
    }

    if (!isOpen && !isAnimating) return null

    return (
        <div
            ref={menuRef}
            className="absolute top-12 w-[600px] bg-white dark:bg-black rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 z-50 p-4 transition-all duration-200 ease-out"
            style={{
                ...positionStyle,
                animation: isOpen ? 'slideDown 0.2s ease-out' : 'slideUp 0.2s ease-out forwards',
            }}
            onAnimationEnd={handleAnimationEnd}
        >
            <div className="relative mb-4 group">
                {/* Gradient border wrapper */}
                <div className="rounded-full p-[1px] bg-gray-200 dark:bg-gray-800 group-focus-within:dark:bg-gradient-to-br group-focus-within:dark:from-blue-400/40 group-focus-within:dark:via-purple-500/40 group-focus-within:dark:to-pink-400/30 transition-all duration-200">
                    <div className="relative bg-white dark:bg-gray-950 rounded-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 z-10" />
                        <Input
                            placeholder="Buscar visualizações..."
                            className="pl-9 h-7 bg-transparent border-0 rounded-full dark:text-white dark:placeholder-gray-500 focus-visible:ring-0 text-sm"
                            autoFocus
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                {/* Popular Section */}
                <div>
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 px-1">Popular</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <ViewItem
                            icon={List}
                            color="text-gray-600"
                            bgColor="bg-gray-100"
                            title="Lista"
                            description="Acompanhe tarefas, erros, pessoas ..."
                        />
                        <ViewItem
                            icon={BarChart2}
                            color="text-white"
                            bgColor="bg-red-500"
                            title="Gantt"
                            description="Planeje dependências e tempo"
                        />
                        <ViewItem
                            icon={Calendar}
                            color="text-white"
                            bgColor="bg-orange-500"
                            title="Calendário"
                            description="Planeje, agende e delegue"
                        />
                        <ViewItem
                            icon={FileText}
                            color="text-white"
                            bgColor="bg-blue-500"
                            title="Documento"
                            description="Colabore e documente qualquer c..."
                        />
                        <ViewItem
                            icon={Layout}
                            color="text-white"
                            bgColor="bg-blue-600"
                            title="Quadro - Kanban"
                            description="Mova tarefas entre colunas"
                        />
                        <ViewItem
                            icon={ClipboardList}
                            color="text-white"
                            bgColor="bg-purple-600"
                            title="Formulário"
                            description="Colete, monitore e faça o relatório ..."
                        />
                    </div>
                </div>

                {/* More Views Section */}
                <div>
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 px-1">Mais visualizações</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <ViewItemSimple icon={Table} color="text-green-600" title="Tabela" description="Formato de tabela estruturada" />
                        <ViewItemSimple icon={Clock} color="text-orange-600" title="Linha do tempo" description="Veja as tarefas por data de início e ..." />
                        <ViewItemSimple icon={Activity} color="text-blue-500" title="Atividade" description="Feed de atividades em tempo real" />
                        <ViewItemSimple icon={BarChart2} color="text-green-600" title="Carga de trabalho" description="Visualize a capacidade da equipe" />
                        <ViewItemSimple icon={Monitor} color="text-yellow-500" title="Whiteboard" description="Visualize e faça um brainstorming ..." />
                        <ViewItemSimple icon={Users} color="text-purple-600" title="Equipe" description="Monitore o trabalho que está send..." />
                        <ViewItemSimple icon={Map} color="text-pink-500" title="Mapa mental" description="Brainstorming visual de ideias" />
                        <ViewItemSimple icon={Map} color="text-orange-500" title="Mapa" description="Tarefas visualizadas por endereço" />
                    </div>
                </div>

                {/* Integrations Section */}
                <div>
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 px-1">Incorporações</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <ViewItemSimple icon={Globe} color="text-gray-600" title="Qualquer site" />
                        <ViewItemSimple icon={FileSpreadsheet} color="text-green-600" title="Google Sheets" />
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Visualização privada
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Fixar visualização
                </label>
            </div>
        </div>
    )
}


